import { useAuth0, User as AuthProfile } from '@auth0/auth0-react';
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { addAuthInterceptor } from '../api';
import ResendEmailVerification from '../features/ResendEmailVerification';

let mock: Record<string, string | boolean | Record<string, string | Array<unknown> | boolean>>;
if (process.env.REACT_APP_MOCK_USER === 'true') {
    mock = {
        'https://www.cimplicodev.com/uuid': '00000000-0000-0000-0000-000000000000',
        'https://www.cimplicodev.com/email': 'fake@cimplico.com',
        'https://www.cimplicodev.com/workpapers': {
            firms: [
                {
                    id: '11111111-1111-1111-1111-111111111111',
                    shortId: 'Fh-IY9vP',
                },
                {
                    id: '22222222-2222-2222-2222-222222222222',
                    shortId: 'EGY2bJpT',
                },
                {
                    id: '33333333-3333-3333-3333-333333333333',
                    shortId: 'ri5pCifb',
                },
            ],
        },
        nickname: 'demo',
        name: 'demo@cimplico.com',
        updated_at: '2022-06-29T22:46:10.898Z',
        email: 'developer@cimplico.com',
        email_verified: false,
    };
}

const AuthProfileContext = createContext<AuthProfile | undefined>(undefined);

type AuthProfileProviderProps = {
    children: ReactNode | ReactNode[];
};

function AuthProfileProvider({ children }: AuthProfileProviderProps): JSX.Element {
    const [loading, setLoading] = useState(true);
    const { user, isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently, handleRedirectCallback } = useAuth0();

    const [params] = useSearchParams();
    const profile = useMemo(() => {
        return mock || user;
    }, [user]);

    const navigate = useNavigate();
    useEffect(() => {
        if (mock) {
            setLoading(true);
            addAuthInterceptor(async () => '');
            setTimeout(() => {
                setLoading(false);
            }, 1000);
            return;
        }

        if (isAuthenticated && profile) {
            addAuthInterceptor(getAccessTokenSilently);
            // handleRedirectCallback().then(console.log);
        }
        // This is an unfortunate hack to prevent api calls from firing before an access token is fetched.
        // Axios' recommended methods do not currently work. This may need to be changed into a custom hook at a later date.
        setTimeout(() => {
            setLoading(false);
        }, 1000);
    }, [profile, isAuthenticated, getAccessTokenSilently]);

    if (window.location.pathname === '/xero-refer/signup') {
        loginWithRedirect({ authorizationParams: { connection: 'Xero', redirect_uri: `${window.location.origin}?xeroRefer=true` } });
        return <></>;
    }

    if (mock) {
        return <AuthProfileContext.Provider value={profile}>{children}</AuthProfileContext.Provider>;
    }

    if (isLoading) {
        return <>Loading...</>;
    }
    if (loading) {
        return <></>;
    }

    if (!isAuthenticated || !profile) {
        if (params.get('error_description')) {
            if (params.get('error_description') === 'Please verify your email address before logging in') {
                return <ResendEmailVerification />;
            }
            return <p>{params.get('error_description')}</p>;
        } else if (window.location.pathname === '/accept-invitation') {
            const invitationCode = params.get('invitation');
            if (invitationCode) {
                // Save the invitation id in local storage
                localStorage.setItem('invitation-id', invitationCode);
            }
            loginWithRedirect({ authorizationParams: { redirectUri: window.location.href } });
            return <></>;
        }
        loginWithRedirect();
        return <></>;
    }

    return <AuthProfileContext.Provider value={profile}>{children}</AuthProfileContext.Provider>;
}

function useAuthProfileContext(): AuthProfile {
    const context = useContext(AuthProfileContext);
    if (context === undefined) {
        throw new Error('useAuthProfileContext must be used within a AuthProfileProvider');
    }
    return context;
}

export { AuthProfileProvider, useAuthProfileContext };
